/**
 * SettingsDenied (M286, settings audit idea 10): the one "Not authorized" state of a settings page, inside its
 * SettingsPageShell so the breadcrumb still leads back, and the URL stays put instead of bouncing to /dashboard.
 * `permission` is a feature-catalog key (named by its label and key) or, for a rule that is not one key, the words
 * for it ("the Admin or Super Admin role"). The settings routes in App.tsx render it as their fallback, and pages
 * that refuse on their own render it too; /unauthorized stays for routes outside settings.
 */
import { AlertTriangle } from 'lucide-react';
import { FEATURE_CATALOG } from '@aiga/app-config/permissions';
import { SettingsPageShell, type SettingsCrumb, type SettingsPageWidth } from './SettingsPageShell';

interface SettingsDeniedProps {
  title: string;
  permission: string;
  crumbs?: SettingsCrumb[];
  root?: SettingsCrumb;
  width?: SettingsPageWidth;
}

export function SettingsDenied({ title, permission, crumbs, root, width = '2xl' }: SettingsDeniedProps) {
  const feature = FEATURE_CATALOG.find((f) => f.key === permission);
  return (
    <SettingsPageShell title={title} crumbs={crumbs} root={root} width={width}>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 flex gap-3" data-testid="settings-denied">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <h2 className="text-base font-semibold text-amber-900">Not authorized</h2>
          <p className="text-sm text-amber-800 mt-1">
            This page needs{' '}
            {feature ? (
              <>
                the <strong>{feature.label}</strong> permission (<code className="text-xs">feature:{feature.key}</code>)
              </>
            ) : (
              <strong>{permission}</strong>
            )}
            . If you need access, ask an admin to change your role.
          </p>
        </div>
      </div>
    </SettingsPageShell>
  );
}
